// @src/pre-deployment/src/components/EmailEntry.js v1.0
// - Email entry page for pre-deployment environment
// - Integrates Firebase email link authentication
// - Handles both login and registration flows

import { signInWithEmailAndPassword } from 'firebase/auth';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/firebase';

const EmailEntry = () => {


  const [activeTab, setActiveTab] = useState('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { sendRegistrationLink, refreshProfile, user, userProfile } = useAuth();
  const navigate = useNavigate();

  console.group('📧 PRE-DEPLOYMENT EMAIL ENTRY: Component loaded');
  console.log('Active tab:', activeTab);
  console.log('Current user:', user?.uid);
  console.log('User profile:', userProfile);
  console.groupEnd();



  // Redirect already authenticated users (only if fully registered)
  useEffect(() => {
    if (user && userProfile && userProfile.passwordSet && userProfile.profileComplete) {
      console.log('📧 PRE-DEPLOYMENT EMAIL ENTRY: User fully registered, redirecting to app homepage');
      navigate('/app-homepage');
    }
  }, [user, userProfile, navigate]);

  const handleTabSwitch = (tab) => {
    console.log('🔄 PRE-DEPLOYMENT EMAIL ENTRY: Switching to tab:', tab);
    setActiveTab(tab);
    setMessage('');
    setEmail('');
    setPassword('');
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    console.group('📧 PRE-DEPLOYMENT EMAIL ENTRY: Registration form submitted');
    console.log('Email:', email);

    setIsLoading(true);
    setMessage('');

    try {
      const result = await sendRegistrationLink(email);

      if (result.success) {
        setMessage('Success! Check your email for the sign-in link to continue registration.');
        console.log('Registration link sent successfully');
      } else {
        if (result.userExists) {
          setMessage('This email is already registered. Please use the Login tab instead.');
        } else {
          setMessage(`Error: ${result.error}`);
        }
        console.error('Registration link failed:', result.error);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      console.groupEnd();
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    console.group('🔐 PRE-DEPLOYMENT EMAIL ENTRY: Login form submitted');
    console.log('Email:', email);

    setIsLoading(true);
    setMessage('');

    try {
      // Attempt to sign in with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful:', userCredential.user.uid);

      // Refresh profile to get latest data
      await refreshProfile();

      // Navigate to app homepage
      console.log('Redirecting to app homepage');
      navigate('/app-homepage');

    } catch (error) {
      console.error('Login error:', error);

      // Handle specific Firebase auth errors
      let errorMessage = 'Login failed. Please check your credentials.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please register first.';
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }

      setMessage(errorMessage);
    } finally {
      setIsLoading(false);
      console.groupEnd();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="container">
      <div className="logo-section">
        <img src="/img/logo.png" alt="Logo" className="logo" />
        <h1>Pre-Deployment</h1>
        <p>Testing Environment</p>
      </div>

      <div className="auth-section">
        {/* Tab Navigation */}
        <div className="tab-nav">
          <div
            className={`tab-item ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('login')}
          >
            Login
          </div>
          <div
            className={`tab-item ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => handleTabSwitch('register')}
          >
            Register
          </div>
        </div>

        {/* Login Form */}
        {activeTab === 'login' && (
          <div className="auth-form active">
            <form onSubmit={handleLoginSubmit}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
                <i
                  className={`fas ${showPassword ? 'fa-eye' : 'fa-eye-slash'} password-toggle`}
                  onClick={togglePasswordVisibility}
                ></i>
              </div>
              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        )}

        {/* Register Form */}
        {activeTab === 'register' && (
          <div className="auth-form active">
            <form onSubmit={handleRegisterSubmit}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Sending Link...' : 'Continue'}
              </button>
            </form>
          </div>
        )}

        {message && (
          <div className={`message ${message.includes('Success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailEntry;
